import { firebaseAuth } from '../auth/firebase';
import { getDevSession } from '../auth/dev-session';

export type Service = 'identity' | 'diya' | 'engagement' | 'content' | 'tulsi' | 'mandir';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly extensions?: Record<string, unknown>,
  ) {
    super(message);
  }
}

const GATEWAY = (import.meta.env.VITE_GATEWAY_URL as string | undefined) || '/graphql';
const PERSISTED = import.meta.env.VITE_PERSISTED_QUERIES === 'true';

async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface GqlOptions {
  /** Send without the Firebase ID token. */
  public?: boolean;
  appCheckToken?: string;
}

/**
 * GraphQL over the Apollo Router. `service` only matters for the dev bypass, where each operation goes
 * straight to the subgraph that owns it; every operation in this app is scoped to a single subgraph.
 */
export async function gql<T>(service: Service, query: string, variables?: Record<string, unknown>, opts: GqlOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    'accept-language': 'en',
    'x-request-id': crypto.randomUUID(),
  };
  // compile-time guard: the whole dev branch is dropped from production bundles
  const dev = import.meta.env.DEV ? getDevSession() : null;
  let url = GATEWAY;
  if (dev) {
    url = `/sg/${service}/graphql`;
    headers['x-user-id'] = dev.userId;
    headers['x-user-role'] = dev.role;
  } else if (!opts.public) {
    const user = firebaseAuth()?.currentUser;
    if (!user) throw new ApiError('Not signed in', 'UNAUTHENTICATED');
    headers.authorization = `Bearer ${await user.getIdToken()}`;
  }
  if (opts.appCheckToken) headers['x-firebase-appcheck'] = opts.appCheckToken;

  const body = PERSISTED && !dev
    ? { variables, extensions: { persistedQuery: { version: 1, sha256Hash: await sha256(query) } } }
    : { query, variables };

  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
  let json: { data?: T; errors?: { message: string; extensions?: Record<string, unknown> }[] };
  try {
    json = await res.json();
  } catch {
    throw new ApiError(`Request failed (${res.status})`, 'NETWORK');
  }
  if (json.errors?.length) {
    const e = json.errors[0];
    throw new ApiError(e.message, e.extensions?.code as string | undefined, e.extensions);
  }
  if (!json.data) throw new ApiError('Empty response', 'EMPTY');
  return json.data;
}
